import type {
  ApiErrorBody,
  AttachResponse,
  AuthExchangeResponse,
  ConfigResponse,
  CreateRoomRequest,
  DevLoginResponse,
  ErrorCode,
  HistoryResponse,
  LeaderboardResponse,
  MatchResultResponse,
  MatchStateResponse,
  MeResponse,
  MoveRequest,
  MoveResponse,
  MyRoomsResponse,
  PlayerStatsDto,
  RematchKind,
  RoomPreviewDto,
  RoomResponse,
  ShareResponse,
  StartRoomResponse,
  TimeResponse,
} from '@chromarble/shared-types';

/** Base URL of the game API: "/api" for the Node host, or the Supabase function URL. */
export const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/+$/, '') || '/api';

export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | 'NETWORK',
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
  get isNetwork(): boolean {
    return this.code === 'NETWORK';
  }
}

let authToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

export function setAuthToken(token: string | null): void {
  authToken = token;
}

export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

async function request<T>(method: 'GET' | 'POST', path: string, body?: unknown, timeoutMs = 10_000): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      body: method === 'POST' ? JSON.stringify(body ?? {}) : undefined,
      signal: controller.signal,
      cache: 'no-store',
    });
  } catch {
    throw new ApiClientError(0, 'NETWORK', '네트워크 연결을 확인하세요.');
  } finally {
    clearTimeout(timer);
  }
  let data: unknown = null;
  try {
    data = await res.json();
  } catch {
    /* empty body */
  }
  if (!res.ok) {
    const err = (data as ApiErrorBody | null)?.error;
    if (res.status === 401 && authToken) onUnauthorized?.();
    if (res.status >= 500 && !err) throw new ApiClientError(res.status, 'NETWORK', '서버에 연결할 수 없습니다.');
    throw new ApiClientError(res.status, err?.code ?? 'INTERNAL', err?.message ?? `HTTP ${res.status}`, err?.details);
  }
  return data as T;
}

const enc = encodeURIComponent;

export const api = {
  config: () => request<ConfigResponse>('GET', '/config'),
  time: () => request<TimeResponse>('GET', '/time', undefined, 5_000),
  devLogin: (tenantId: string, crewUserId: string, nickname: string) =>
    request<DevLoginResponse>('POST', '/auth/dev/login', { tenantId, crewUserId, nickname }),
  exchange: (code: string) => request<AuthExchangeResponse>('POST', '/auth/crew/exchange', { code }),
  me: () => request<MeResponse>('GET', '/me'),

  myRooms: () => request<MyRoomsResponse>('GET', '/rooms'),
  createRoom: (body: CreateRoomRequest) => request<RoomResponse>('POST', '/rooms', body),
  room: (roomId: string) => request<RoomResponse>('GET', `/rooms/${enc(roomId)}`),
  preview: (roomId: string, invite: string) =>
    request<{ room: RoomPreviewDto; serverTime: number }>('GET', `/rooms/${enc(roomId)}/preview?invite=${enc(invite)}`),
  join: (roomId: string, inviteToken: string) => request<RoomResponse>('POST', `/rooms/${enc(roomId)}/join`, { inviteToken }),
  leave: (roomId: string) => request<{ left: boolean }>('POST', `/rooms/${enc(roomId)}/leave`),
  ready: (roomId: string, ready: boolean) => request<RoomResponse>('POST', `/rooms/${enc(roomId)}/ready`, { ready }),
  start: (roomId: string) => request<StartRoomResponse>('POST', `/rooms/${enc(roomId)}/start`),
  rematch: (roomId: string, kind: RematchKind) => request<RoomResponse>('POST', `/rooms/${enc(roomId)}/rematch`, { kind }),
  shareRoom: (roomId: string) => request<ShareResponse>('POST', `/rooms/${enc(roomId)}/share`),

  matchState: (matchId: string) => request<MatchStateResponse>('GET', `/matches/${enc(matchId)}/state`),
  attach: (matchId: string) => request<AttachResponse>('POST', `/matches/${enc(matchId)}/attach`),
  move: (matchId: string, body: MoveRequest) => request<MoveResponse>('POST', `/matches/${enc(matchId)}/moves`, body, 8_000),
  forfeit: (matchId: string) => request<MatchStateResponse>('POST', `/matches/${enc(matchId)}/forfeit`),
  result: (matchId: string) => request<MatchResultResponse>('GET', `/matches/${enc(matchId)}/result`),
  shareResult: (matchId: string) => request<ShareResponse>('POST', `/matches/${enc(matchId)}/share-result`),

  stats: (userId: string) => request<PlayerStatsDto>('GET', `/players/${enc(userId)}/stats`),
  history: (userId: string, cursor?: string | null) =>
    request<HistoryResponse>('GET', `/players/${enc(userId)}/history${cursor ? `?cursor=${enc(cursor)}` : ''}`),
  leaderboard: (period: 'weekly' | 'all') => request<LeaderboardResponse>('GET', `/leaderboards/crew?period=${period}`),
};

export function errorMessage(e: unknown): string {
  if (e instanceof ApiClientError) return e.message;
  if (e instanceof Error) return e.message;
  return '알 수 없는 오류가 발생했습니다.';
}
